/**
 * Side drawer + table-drawer triggers for Specs / Size / What's in the Box
 * and accessory detail drawers.
 */
(function () {
  const trapFocusHandlers = {};
  const BODY_OPEN_CLASS = 'h-drawer-open';
  const TRANSITION_MS = 600;

  function removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
    if (elementToFocus) elementToFocus.focus();
  }

  function trapFocus(container, elementToFocus = container) {
    const focusableEls = Array.from(
      container.querySelectorAll(
        'summary, a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])'
      )
    );
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];
    removeTrapFocus();
    trapFocusHandlers.focusin = (evt) => {
      if (evt.target === container || evt.target === lastEl || evt.target === firstEl) {
        document.addEventListener('keydown', trapFocusHandlers.keydown);
      }
    };
    trapFocusHandlers.focusout = () => {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };
    trapFocusHandlers.keydown = (evt) => {
      if (evt.code !== 'Tab') return;
      if (evt.target === lastEl && !evt.shiftKey) {
        evt.preventDefault();
        firstEl?.focus();
      }
      if ((evt.target === container || evt.target === firstEl) && evt.shiftKey) {
        evt.preventDefault();
        lastEl?.focus();
      }
    };
    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);
    (elementToFocus || container).focus({ preventScroll: true });
  }

  function ensureOverlay() {
    let overlay = document.querySelector('.js-overlay.accessory-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'accessory-overlay js-overlay';
      document.body.appendChild(overlay);
    } else if (overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }
    return overlay;
  }

  function setViewportHeight() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }

  function lockScroll() {
    if (document.body.classList.contains(BODY_OPEN_CLASS)) return;
    document.body.dataset.hDrawerScrollY = String(window.scrollY || 0);
    document.body.classList.add(BODY_OPEN_CLASS);
    document.documentElement.classList.add(BODY_OPEN_CLASS);
  }

  function unlockScroll() {
    if (document.querySelector('side-drawer[open]')) return;
    const y = parseInt(document.body.dataset.hDrawerScrollY || '0', 10);
    document.body.classList.remove(BODY_OPEN_CLASS);
    document.documentElement.classList.remove(BODY_OPEN_CLASS);
    delete document.body.dataset.hDrawerScrollY;
    document.documentElement.style.height = '';
    window.scrollTo(0, y);
  }

  function waitForDrawerTransition(drawer) {
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        drawer.removeEventListener('transitionend', onEnd);
        resolve();
      };
      const onEnd = (evt) => {
        if (evt.target !== drawer) return;
        if (evt.propertyName !== 'transform') return;
        finish();
      };
      drawer.addEventListener('transitionend', onEnd);
      setTimeout(finish, TRANSITION_MS + 80);
    });
  }

  if (!customElements.get('side-drawer')) {
    class SideDrawer extends HTMLElement {
      connectedCallback() {
        // Always mount on body so fixed positioning is viewport-relative.
        // Keeping drawers inside sticky product__info leaks a vertical strip on the right.
        this.portalToBody();
        this.overlay = ensureOverlay();
        if (!this.hasAttribute('open')) {
          this.setAttribute('aria-hidden', 'true');
          this.setAttribute('inert', '');
        }
      }

      handleClick(evt) {
        if (evt.target.closest('.js-close-drawer') || evt.target === this.overlay) {
          this.close();
        }
      }

      portalToBody() {
        if (!document.body || this.parentElement === document.body) return;
        document.body.appendChild(this);
      }

      open(opener) {
        if (this.hasAttribute('open') || this._opening) return;

        document.querySelectorAll('side-drawer[open]').forEach((other) => {
          if (other !== this) other.close({ immediate: true });
        });

        this._opening = true;
        this.opener = opener || document.activeElement;
        this.portalToBody();
        this.overlay = ensureOverlay();
        setViewportHeight();
        lockScroll();
        document.documentElement.style.height = '100svh';

        this.clickHandler = this.clickHandler || this.handleClick.bind(this);
        this.keyupHandler =
          this.keyupHandler ||
          ((evt) => {
            if (evt.key === 'Escape') this.close();
          });
        this.addEventListener('click', this.clickHandler);
        this.addEventListener('keyup', this.keyupHandler);
        this.overlay.addEventListener('click', this.clickHandler);

        // Paint off-screen first, then animate in (Concept-style two-phase open).
        this.removeAttribute('open');
        this.removeAttribute('inert');
        this.setAttribute('aria-hidden', 'false');
        void this.offsetWidth;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.overlay.classList.add('is-visible');
            this.setAttribute('open', '');
            this._opening = false;
            trapFocus(this, this.querySelector('.js-close-drawer') || this);
          });
        });
      }

      async close({ immediate = false } = {}) {
        if (!this.hasAttribute('open') && !this._opening) return;
        this._opening = false;

        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');
        this.overlay?.classList.remove('is-visible');

        if (!immediate) {
          await waitForDrawerTransition(this);
        }

        this.setAttribute('inert', '');
        removeTrapFocus(this.opener);
        this.removeEventListener('click', this.clickHandler);
        this.removeEventListener('keyup', this.keyupHandler);
        this.overlay?.removeEventListener('click', this.clickHandler);
        unlockScroll();
      }
    }
    customElements.define('side-drawer', SideDrawer);
  }

  if (!customElements.get('table-drawer')) {
    class TableDrawer extends HTMLElement {
      connectedCallback() {
        const trigger = this.querySelector('.js-table-drawer');
        if (!trigger || trigger.dataset.bound === 'true') return;
        trigger.dataset.bound = 'true';
        trigger.addEventListener('click', (evt) => {
          if (evt.target.closest('.variant-selector')) return;
          const btn = evt.target.closest('.js-table-drawer');
          if (!btn) return;
          const drawer = document.getElementById(`table-${btn.dataset.id}-drawer`);
          drawer?.open?.(btn);
        });
      }
    }
    customElements.define('table-drawer', TableDrawer);
  }

  if (!customElements.get('accessories-bundle')) {
    class AccessoriesBundle extends HTMLElement {
      connectedCallback() {}
    }
    customElements.define('accessories-bundle', AccessoriesBundle);
  }
})();
